/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  ContentScreenDetails,
  TUnifiedCollectionScreenDetails
} from "@tivocorp/data-domain-lib";
import type { Action } from "@tivocorp/data-domain-lib/client-code/actions";

import type { TNetworkOptions } from "../types/TNetworkOptions";

export const CONTENT_DETAIL_FAVORITE_MODE = {
  ADD: "add",
  REMOVE: "remove",
  SEASON: "season",
  YEAR: "year"
} as const;

export type TContentDetailFavoriteTarget =
  | {
      mode: typeof CONTENT_DETAIL_FAVORITE_MODE.ADD;
      contentId: string;
      collectionId: string;
    }
  | { mode: typeof CONTENT_DETAIL_FAVORITE_MODE.REMOVE; contentId: string }
  | {
      mode: typeof CONTENT_DETAIL_FAVORITE_MODE.SEASON;
      unifiedCollectionId: string;
      season: number;
    }
  | {
      mode: typeof CONTENT_DETAIL_FAVORITE_MODE.YEAR;
      unifiedCollectionId: string;
      year: number;
    };

/**
 * Atomic facade for `@tivocorp/data-domain-lib` ActionsService, covering
 * every seed type the Content Detail screen recognizes.
 *
 * The four `contentScreenDetails` variants (content / offer / recording /
 * vodOffer) all return the same `ContentScreenDetails` shape, so callers
 * that hold an opaque seed can go through {@link
 * IContentDetailInteractor.fetchScreenDetailsForSeed} instead of switching
 * on the seed type themselves. Unified collections return a different DTO
 * and stay on their own method.
 */
export interface IContentDetailInteractor {
  updateFavorite(
    target: TContentDetailFavoriteTarget,
    options?: TNetworkOptions
  ): Promise<void>;

  /**
   * Fetches ContentScreenDetails (action buttons + tab structure) for a
   * single content item. GET /v3/contentScreenDetails/content/{contentId}
   *
   * @param contentId - The content ID
   * @param isForced - Force a fresh fetch, bypassing cache
   * @param options - Per-call network options (signal, retry)
   */
  fetchContentScreenDetails(
    contentId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails>;

  /**
   * Fetches ContentScreenDetails for a linear/streaming offer.
   * GET /v3/contentScreenDetails/offer/{offerId}
   *
   * @param offerId - The offer ID
   * @param isForced - Force a fresh fetch, bypassing cache
   * @param options - Per-call network options (signal, retry)
   */
  fetchOfferScreenDetails(
    offerId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails>;

  /**
   * Fetches ContentScreenDetails for a recording.
   * GET /v3/contentScreenDetails/recording/{recordingId}
   *
   * @param recordingId - The recording ID
   * @param isForced - Force a fresh fetch, bypassing cache
   * @param options - Per-call network options (signal, retry)
   */
  fetchRecordingScreenDetails(
    recordingId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails>;

  /**
   * Fetches ContentScreenDetails for a VOD offer.
   * GET /v2/contentScreenDetails/vodOffer/{vodOfferId}
   *
   * The VOD variant has no V3 operation in the generated client, so this is
   * the V2 call — the response shape is identical.
   *
   * @param vodOfferId - The VOD offer ID
   * @param isForced - Force a fresh fetch, bypassing cache
   * @param options - Per-call network options (signal, retry)
   */
  fetchVodOfferScreenDetails(
    vodOfferId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails>;

  /**
   * Dispatches to whichever `contentScreenDetails` operation matches the
   * seed type. `unifiedCollectionId` is NOT handled here — it returns a
   * different DTO — so it falls through to the content-id operation and
   * callers must route it to
   * {@link IContentDetailInteractor.fetchUnifiedCollectionScreenDetails}
   * themselves.
   *
   * @param seed - The navigation seed
   * @param isForced - Force a fresh fetch, bypassing cache
   * @param options - Per-call network options (signal, retry)
   */
  fetchScreenDetailsForSeed(
    seed: { seedType: string; seedId: string },
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails>;

  /**
   * Fetches TUnifiedCollectionScreenDetails (action buttons + tab
   * structure) for a series/team/league unified collection.
   * GET /v3/unifiedCollectionScreenDetails/unifiedCollection/{unifiedCollectionId}
   *
   * @param unifiedCollectionId - The unified collection ID
   * @param isForced - Force a fresh fetch, bypassing cache
   * @param options - Per-call network options (signal, retry)
   */
  fetchUnifiedCollectionScreenDetails(
    unifiedCollectionId: string,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TUnifiedCollectionScreenDetails>;

  /**
   * Resolves the playable-episode watch actions for a series/unified
   * collection (the `unifiedCollectionActionsGet` operation described in
   * docs/unity-client-doc/doc/screens/content/actions/00-actions.md,
   * "Series Playable-Episode Action Resolution").
   *
   * TODO: not yet available in @tivocorp/data-domain-lib — replace with
   * the dedicated playable-episode resolver once it exists; the mapper
   * falls back to actionUnifiedCollectionDetail.actions from the
   * screen-details response.
   *
   * @param unifiedCollectionId - The unified collection ID
   * @param options - Per-call network options (signal, retry)
   * @returns Always resolves to `null` until the resolver exists.
   */
  resolvePlayableEpisodeActions(
    unifiedCollectionId: string,
    options?: TNetworkOptions
  ): Promise<Action[] | null>;
}
